import React, { Suspense, lazy, startTransition, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Sparkles, Tag } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import StoreStructuredData from '../components/StoreStructuredData';
import HomeCampaign from '../components/home/HomeCampaign';
import MarketHero from '../components/home/MarketHero';
import HomeBenefits from '../components/home/HomeBenefits';
import HomeCategoryTiles from '../components/home/HomeCategoryTiles';
import LuxuryProductCard from '../components/LuxuryProductCard';
import NearMount from '../components/premium/NearMount';
import { useLanguage } from '../contexts/LanguageContext';
import { getProducts } from '../utils/api';
import { loadForYou } from '../utils/forYou';
import { onHeroPhotoDone } from '../utils/heroHint';
import ForYouReason from '../components/home/ForYouReason';
import { useVatRegistered } from '../contexts/StoreFactsContext';

// Quick view loads on first use (keeps it out of the main bundle).
const QuickViewModal = lazy(() => import('../components/QuickViewModal'));
// The sale band shows only while a flash sale runs, below the hero, benefits
// and category tiles: its own chunk (it renders nothing until its answer is in).
const HomeFlashSale = lazy(() => import('../components/home/HomeFlashSale'));
// Far below the fold (one "home-below" chunk): the code loads when the
// shopper scrolls near it, so it stays out of main.js and the first paint.
const HomeOccasions = lazy(() => import(/* webpackChunkName: "home-below" */ '../components/home/HomeOccasions'));
const HomeBrandsStrip = lazy(() => import(/* webpackChunkName: "home-below" */ '../components/home/HomeBrandsStrip'));
const HomeClosing = lazy(() => import(/* webpackChunkName: "home-below" */ '../components/home/HomeClosing'));

/** Longest wait for the hero photo before the rest of the page loads its own. */
export const HERO_WAIT_MS = 4000;

/** Pieces per feed tab: two full rows of five on wide screens. */
export const FEATURED_LIMIT = 10;

/**
 * Feed tabs (SHEIN "For you | New in | Deals"): each is a real query.
 * For you = GET /products/for-you (the shopper's views, wishlist, orders,
 * colours and size; the featured pieces on an API without it), New in =
 * newest first,
 * Deals = pieces whose
 * real price is below their regular price (on_sale).
 */
export const FEED_TABS = [
  { id: 'foryou', icon: null, label: { en: 'For you', ar: 'لك' }, params: { is_featured: true }, more: '/products' },
  { id: 'new', icon: Sparkles, label: { en: 'New in', ar: 'وصل حديثًا' }, params: { sort: 'newest' }, more: '/products' },
  { id: 'deals', icon: Tag, label: { en: 'Deals', ar: 'عروض' }, params: { on_sale: true }, more: '/products?sale=true' },
];

const Home = () => {
  const { t } = useLanguage();
  // "Prices include VAT" only while the store is VAT-registered.
  const vatRegistered = useVatRegistered();
  const [tab, setTab] = useState('foryou');
  const [featured, setFeatured] = useState([]);
  // 'loading' | 'ready' | 'empty' | 'error'. No placeholder products: a
  // failed or empty response shows an honest state instead of fake pieces.
  const [status, setStatus] = useState('loading');
  const [quickView, setQuickView] = useState(null);
  // The hero photo is this page's largest paint: the feed's photos and the
  // blocks further down wait for it (at most HERO_WAIT_MS), so on a slow
  // connection it does not share the line with a dozen other images.
  const [heroDone, setHeroDone] = useState(false);
  useEffect(() => {
    let alive = true;
    const done = () => { if (alive) setHeroDone(true); };
    const stop = onHeroPhotoDone(done);
    const timer = setTimeout(done, HERO_WAIT_MS);
    return () => { alive = false; stop(); clearTimeout(timer); };
  }, []);
  const loading = status === 'loading';
  const current = FEED_TABS.find((x) => x.id === tab) || FEED_TABS[0];

  // Waking-up hint when the first attempt is slow (Render cold start).
  const [slow, setSlow] = useState(false);

  const loadFeed = useCallback(() => {
    let cancelled = false;
    setStatus('loading');
    setSlow(false);
    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 6000);
    // getProducts sends the customer token (if signed in) so VIP / wholesale
    // shoppers get their own pricing.tier_price; it times out, retries once
    // and shares one request between identical concurrent calls.
    const featuredEdit = () => getProducts({ ...current.params, limit: FEATURED_LIMIT });
    (current.id === 'foryou'
      // An API without the feed yet (404) → the featured edit.
      ? loadForYou({ limit: FEATURED_LIMIT }).catch((err) => (err?.status === 404 ? featuredEdit() : Promise.reject(err)))
      : featuredEdit())
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : data?.items ?? [];
        // Ten cards below the hero: rendered in small slices (a transition)
        // so a slow phone stays responsive while they are built.
        startTransition(() => {
          setFeatured(items);
          setStatus(items.length ? 'ready' : 'empty');
        });
      })
      .catch(() => {
        if (cancelled) return;
        setFeatured([]);
        setStatus('error');
      })
      .finally(() => clearTimeout(slowTimer));
    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [current]);

  useEffect(() => loadFeed(), [loadFeed]);

  return (
    <div className="pb-6">
      <SEOHead
        title="ANUM 360 Gallery | Designer Bags, Jewellery & Oud UAE"
        titleAr="ANUM 360 Gallery | حقائب فاخرة ومجوهرات وعطور عود"
        description={vatRegistered
          ? 'Shop authentic designer bags, fine jewellery, oud perfumes and fashion at ANUM 360 Gallery, Abu Dhabi. UAE-wide delivery, prices include VAT.'
          : 'Shop authentic designer bags, fine jewellery, oud perfumes and fashion at ANUM 360 Gallery, Abu Dhabi, with UAE-wide delivery.'}
        descriptionAr={vatRegistered
          ? 'تسوّق حقائب مصممين أصلية ومجوهرات فاخرة وعطور عود وأزياء في ANUM 360 Gallery أبوظبي، مع التوصيل في جميع أنحاء الإمارات والأسعار شاملة الضريبة.'
          : 'تسوّق حقائب مصممين أصلية ومجوهرات فاخرة وعطور عود وأزياء في ANUM 360 Gallery أبوظبي، مع التوصيل في جميع أنحاء الإمارات.'}
        keywords="luxury fashion UAE, designer bags Abu Dhabi, fine jewellery UAE, oud perfumes, luxury gifts"
        url="/"
      />
      <StoreStructuredData />
      <h1 className="sr-only">
        {t({ en: 'ANUM 360 Gallery: authentic designer bags, fine jewellery, oud perfumes and fashion in Abu Dhabi, UAE', ar: 'ANUM 360 Gallery: حقائب مصممين أصلية ومجوهرات فاخرة وعطور عود وأزياء في أبوظبي، الإمارات' })}
      </h1>

      {/* Marketplace home (SHEIN / Temu / Noon): colour hero → benefits →
          round category tiles → live sale (only when one runs) → feed tabs
          and a dense grid → occasion blocks → brands → VIP + stylist → letter. */}
      <HomeCampaign />
      <MarketHero />
      <HomeBenefits />
      <HomeCategoryTiles />
      <div className="pt-6 empty:hidden lg:pt-10">
        <Suspense fallback={null}><HomeFlashSale /></Suspense>
      </div>

      {/* Feed */}
      <section className="anum-cv-feed luxury-stage-wide pt-6 lg:pt-10" aria-labelledby="home-feed" aria-busy={loading || undefined}>
        <h2 id="home-feed" className="sr-only">{t({ en: 'Shop the feed', ar: 'تسوق القطع' })}</h2>
        {/* Phones: pill tabs. Desktop: text tabs on a rule, like a store's section switcher. */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 anum-no-scrollbar lg:mb-4 lg:gap-6 lg:border-b lg:border-rule lg:pb-0" role="group" aria-label={t({ en: 'Show', ar: 'عرض' })}>
          {FEED_TABS.map((item) => {
            const Icon = item.icon;
            const on = item.id === tab;
            return (
              <button
                key={item.id}
                type="button"
                aria-pressed={on}
                onClick={() => setTab(item.id)}
                className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg px-5 text-[15px] font-bold transition-colors motion-reduce:transition-none lg:-mb-px lg:rounded-none lg:border-b-2 lg:bg-transparent lg:px-0 lg:text-[18px] ${
                  on ? 'bg-ink text-white dark:bg-white dark:text-ink lg:border-crimson lg:bg-transparent lg:text-foreground dark:lg:bg-transparent dark:lg:text-white' : 'bg-panel text-foreground hover:bg-tint-blush lg:border-transparent lg:bg-transparent lg:text-stone'
                }`}
              >
                {Icon && <Icon size={16} strokeWidth={2.2} aria-hidden="true" className={`lg:hidden ${on ? '' : 'text-crimson-ink'}`} />}
                {t(item.label)}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3 lg:grid-cols-4 xl:grid-cols-5" role="status">
            <span className={slow ? 'col-span-full text-[15px] text-stone' : 'sr-only'}>
              {slow
                ? t({ en: 'Our boutique is waking up. Your pieces will appear in a moment.', ar: 'متجرنا قيد التشغيل. ستظهر القطع خلال لحظات.' })
                : t({ en: 'Loading pieces', ar: 'جارٍ تحميل القطع' })}
            </span>
            {[0, 1, 2, 3].map((value) => (
              <div key={value} aria-hidden="true" className="overflow-hidden rounded-xl">
                <div className="atelier-skeleton" style={{ aspectRatio: '4 / 5' }} />
                <div className="atelier-skeleton mt-2 h-3 w-16" />
                <div className="atelier-skeleton mt-2 h-3.5 w-3/4" />
              </div>
            ))}
          </div>
        ) : status === 'error' ? (
          <div className="rounded-2xl border border-rule px-6 py-12 text-center" role="alert">
            <AlertTriangle size={32} strokeWidth={1.8} className="mx-auto text-crimson-ink" aria-hidden="true" />
            <h3 className="mt-4 font-sans text-[1.5rem] font-bold text-foreground">
              {t({ en: 'We couldn’t load these pieces', ar: 'تعذر تحميل هذه القطع' })}
            </h3>
            <p className="mx-auto mt-2 max-w-sm text-[15px] text-stone">
              {t({
                en: 'Our boutique may be waking up. Please try again in a moment, or browse the full collection.',
                ar: 'قد يكون متجرنا قيد التشغيل. يرجى المحاولة بعد لحظات، أو تصفح المجموعة كاملة.',
              })}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <button type="button" onClick={loadFeed} className="inline-flex min-h-[48px] items-center rounded-full bg-crimson px-6 text-[15px] font-bold text-white hover:bg-crimson-deep">
                {t({ en: 'Try again', ar: 'حاول مجددًا' })}
              </button>
              <Link to="/products" className="inline-flex min-h-[44px] items-center text-[15px] font-semibold text-foreground underline underline-offset-4">
                {t({ en: 'Browse the collection', ar: 'تصفح المجموعة' })}
              </Link>
            </div>
          </div>
        ) : status === 'empty' ? (
          <div className="rounded-2xl border border-rule px-6 py-12 text-center">
            <h3 className="font-sans text-[1.5rem] font-bold text-foreground">
              {tab === 'deals'
                ? t({ en: 'No deals right now', ar: 'لا توجد عروض الآن' })
                : t({ en: 'New pieces are being selected', ar: 'نختار قطعًا جديدة' })}
            </h3>
            <p className="mx-auto mt-2 max-w-sm text-[15px] text-stone">
              {tab === 'deals'
                ? t({ en: 'Nothing is reduced at the moment. The full collection is here.', ar: 'لا توجد تخفيضات حاليًا. المجموعة كاملة هنا.' })
                : t({ en: 'Our featured edit is being refreshed. The full collection is available now.', ar: 'نقوم بتحديث القطع المميزة. المجموعة كاملة متاحة الآن.' })}
            </p>
            <Link to="/products" className="mt-6 inline-flex min-h-[48px] items-center rounded-full bg-ink px-6 text-[15px] font-bold text-white dark:bg-white dark:text-ink">
              {t({ en: 'Browse the collection', ar: 'تصفح المجموعة' })}
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3 lg:grid-cols-4 xl:grid-cols-5">
              {/* No `priority` here: the hero is this page's largest paint, so
                  the feed (below it on every screen) must not compete with its
                  photo. */}
              {featured.map((product) => (
                <div key={product.id} className="grid min-w-0 grid-rows-[1fr_auto]">
                  <LuxuryProductCard product={product} onQuickView={setQuickView} deferImage={!heroDone} />
                  {tab === 'foryou' && <ForYouReason item={product} />}
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-center">
              <Link to={current.more} className="inline-flex min-h-[48px] items-center rounded-full border-2 border-foreground px-8 text-[15px] font-bold text-foreground transition-colors hover:border-crimson hover:bg-crimson hover:text-white lg:min-h-[44px] lg:rounded-[4px] lg:border">
                {t({ en: 'See more', ar: 'عرض المزيد' })}
              </Link>
            </div>
          </>
        )}
      </section>

      {/* Mounted near the viewport, and only after the hero photo is in. */}
      {heroDone && (
        <>
          <NearMount margin="1200px 0px">
            <Suspense fallback={null}>
              <HomeOccasions />
            </Suspense>
          </NearMount>

          <NearMount margin="800px 0px">
            <Suspense fallback={null}>
              <HomeBrandsStrip />
            </Suspense>
          </NearMount>

          <NearMount margin="1200px 0px">
            <Suspense fallback={null}>
              <HomeClosing />
            </Suspense>
          </NearMount>
        </>
      )}

      {quickView && (
        <Suspense fallback={null}>
          <QuickViewModal product={quickView} isOpen onClose={() => setQuickView(null)} />
        </Suspense>
      )}
    </div>
  );
};

export default Home;
